// Endless tower generation + ring helpers. Rings are keyed by integer level in a
// Map (worldY = level*LEVEL_GAP) so faller.restIndex/targetIndex are stable level
// numbers — culling passed rings never shifts indices. Generation runs ONLY in the
// bounce phase (main guards this) so a long dive never allocates/pop-ins (ТЗ 3.5.9).
import {
  SECTORS_PER_RING, LEVEL_GAP, GAP_MIN_SECTORS, GAP_MAX_SECTORS,
  RED_PROB_BASE, RED_PROB_MAX, GREEN_PROB, MOVE_AMP, MOVE_SPEED, FORWARD_BUFFER,
} from './config';
import { Ring, Sector, SectorType, MovingSector } from './types';

const TWO_PI = Math.PI * 2;
const DELTA = TWO_PI / SECTORS_PER_RING;

// Deterministic Park–Miller RNG (same family as zen-match levels).
export function createRng(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

export interface Tower {
  rings: Map<number, Ring>;
  generatedTo: number;   // highest level generated
  rng: () => number;
}

// No red on the first couple of platforms; difficulty ramps after that.
function redProb(level: number): number {
  if (level < 2) return 0;
  return Math.min(RED_PROB_BASE + (level - 2) * 0.004, RED_PROB_MAX);
}

function makeSectors(level: number, rng: () => number, allowMoving: boolean, forcedGap?: number): Sector[] {
  const types: SectorType[] = new Array(SECTORS_PER_RING).fill('safe');

  // gaps: 1–2 distinct single-sector openings → always passable
  const gapCount = GAP_MIN_SECTORS + Math.floor(rng() * (GAP_MAX_SECTORS - GAP_MIN_SECTORS + 1));
  const gaps = new Set<number>();
  if (forcedGap !== undefined) gaps.add(forcedGap);
  while (gaps.size < gapCount) gaps.add(Math.floor(rng() * SECTORS_PER_RING));
  for (const g of gaps) types[g] = 'gap';

  // fill the rest with safe / red / green (no green/red on the very first ring)
  const rp = redProb(level);
  const gp = level === 0 ? 0 : GREEN_PROB;
  for (let i = 0; i < SECTORS_PER_RING; i++) {
    if (types[i] === 'gap') continue;
    const r = rng();
    if (r < gp) types[i] = 'green';
    else if (r < gp + rp) types[i] = 'red';
    else types[i] = 'safe';
  }

  const sectors: Sector[] = types.map((type, i) => ({
    type, a0: i * DELTA, a1: (i + 1) * DELTA,
  }));

  // one oscillating sector (after the warm-up window), preferably a non-gap one
  if (allowMoving && rng() < 0.4) {
    const candidates = sectors.map((_, i) => i).filter((i) => types[i] !== 'gap');
    if (candidates.length) {
      const at = candidates[Math.floor(rng() * candidates.length)];
      const mv: MovingSector = { amp: MOVE_AMP, speed: MOVE_SPEED, phase: rng() * TWO_PI };
      sectors[at].move = mv;
    }
  }
  return sectors;
}

function makeRing(level: number, rng: () => number, allowMoving: boolean, forcedGap?: number): Ring {
  return { worldY: level * LEVEL_GAP, sectors: makeSectors(level, rng, allowMoving, forcedGap) };
}

// towerAngle=0 → front local angle = π/2 → sector index 2 (with 8 sectors). The
// faller starts resting on a SAFE sector at FRONT; the guaranteed gap sits a few
// sectors away so the player rotates it under the faller to begin the descent.
const FRONT_SECTOR_AT_START = Math.floor((Math.PI / 2) / DELTA);
const START_GAP = (FRONT_SECTOR_AT_START + 3) % SECTORS_PER_RING;

export function createTower(seed: number, movingAfter: number): Tower {
  const rng = createRng(seed);
  const rings = new Map<number, Ring>();
  rings.set(0, makeRing(0, rng, false, START_GAP));
  const tower: Tower = { rings, generatedTo: 0, rng };
  // pre-fill the forward buffer
  growTo(tower, FORWARD_BUFFER, movingAfter);
  return tower;
}

// Generate rings until `generatedTo` reaches `throughLevel` (inclusive-ish).
export function growTo(tower: Tower, throughLevel: number, movingAfter: number): void {
  while (tower.generatedTo < throughLevel) {
    const lvl = tower.generatedTo + 1;
    tower.rings.set(lvl, makeRing(lvl, tower.rng, lvl >= movingAfter));
    tower.generatedTo = lvl;
  }
}

// Keep the buffer full ahead of the faller (call ONLY while bouncing).
export function ensureBuffer(tower: Tower, targetLevel: number, movingAfter: number): void {
  growTo(tower, targetLevel + FORWARD_BUFFER, movingAfter);
}

// Drop rings that scrolled above the camera (free memory; level keys are stable).
export function cullAbove(tower: Tower, minLevel: number): void {
  for (const lvl of tower.rings.keys()) if (lvl < minLevel) tower.rings.delete(lvl);
}

// Effective angular range of a sector at time t (accounts for oscillation).
export function sectorRange(s: Sector, t: number): [number, number] {
  if (!s.move) return [s.a0, s.a1];
  const off = s.move.amp * Math.sin(t * s.move.speed + s.move.phase);
  return [s.a0 + off, s.a1 + off];
}
